import { useEffect } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { fetchProject } from "@/lib/api";
import { Nav } from "@/components/Nav";
import { VideoPlayer } from "@/components/VideoPlayer";
import { DemoBanner } from "@/components/DemoBanner";
import { PROJECT_DETAIL } from "@/constants/testIds";

const ratioToCss = (r) => (r === "16:9" ? "16/9" : r === "9:16" ? "9/16" : "1/1");

export default function ProjectDetail() {
  const { slug } = useParams();
  const location = useLocation();
  const backHref = `/${location.state?.fromSearch || ""}`;
  const { data, isLoading, isError } = useQuery({
    queryKey: ["project", slug],
    queryFn: () => fetchProject(slug),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-[#101010] text-[#F5F5F0] flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#555550]"
        data-testid="project-loading-state"
      >
        Loading project…
      </div>
    );
  }

  if (isError || !data?.project) {
    return (
      <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid="project-not-found">
        <Nav />
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <p className="font-mono text-xs uppercase tracking-widest text-[#888880]">Project not found</p>
          <Link to="/" className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] hover:underline">
            Back to Work
          </Link>
        </div>
      </div>
    );
  }

  const { project, related, next } = data;

  return (
    <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid="project-detail-page">
      <DemoBanner />
      <Nav />
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 pt-4 sm:pt-6 flex items-center justify-between">
        <Link
          to={backHref}
          data-testid={PROJECT_DETAIL.backButton}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
        >
          <ArrowLeft size={14} /> Back to Work
        </Link>
        {next && (
          <Link
            to={`/work/${next.slug}`}
            data-testid={PROJECT_DETAIL.nextButton}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
          >
            Next: {next.title} <ArrowRight size={14} />
          </Link>
        )}
      </div>

      <div className="mt-4 sm:mt-6">
        <VideoPlayer project={project} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <h1 data-testid={PROJECT_DETAIL.title} className="font-mono text-xl sm:text-2xl tracking-tight text-[#F5F5F0]">
            {project.title}
          </h1>
          {project.isDemo && (
            <span
              data-testid={PROJECT_DETAIL.demoBadge(project.slug)}
              className="font-mono text-[10px] uppercase tracking-widest bg-[#181818] text-[#CCFF00] px-2 py-1 border border-[#CCFF00]/40"
            >
              Demo / Placeholder
            </span>
          )}
        </div>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-widest text-[#888880]">{project.brand}</p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mt-8 pt-6 border-t border-[#2A2A2A] font-mono text-[10px] sm:text-xs uppercase tracking-widest">
          <div>
            <p className="text-[#555550] mb-1">Category</p>
            <p className="text-[#F5F5F0]">{project.category}</p>
          </div>
          <div>
            <p className="text-[#555550] mb-1">Type</p>
            <p className="text-[#F5F5F0]">{project.productionType}</p>
          </div>
          <div>
            <p className="text-[#555550] mb-1">Year</p>
            <p className="text-[#F5F5F0]">{project.year}</p>
          </div>
          <div>
            <p className="text-[#555550] mb-1">Aspect</p>
            <p className="text-[#F5F5F0]">{project.aspectRatio}</p>
          </div>
        </div>

        <p className="mt-8 font-sans text-sm sm:text-base leading-relaxed text-[#c8c8c0] max-w-2xl">{project.description}</p>

        {project.services?.length > 0 && (
          <div className="mt-8">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#555550] mb-2">Services</p>
            <div className="flex flex-wrap gap-2">
              {project.services.map((s) => (
                <span key={s} className="font-mono text-[10px] uppercase tracking-widest border border-[#2A2A2A] px-2 py-1 text-[#888880]">
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}
        {project.keywords?.length > 0 && (
          <div className="mt-4">
            <p className="font-mono text-[10px] uppercase tracking-widest text-[#555550] mb-2">Keywords</p>
            <div className="flex flex-wrap gap-2">
              {project.keywords.map((k) => (
                <span key={k} className="font-mono text-[10px] uppercase tracking-widest text-[#555550]">
                  #{k}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {related?.length > 0 && (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 pb-16 sm:pb-24">
          <p className="font-mono text-[10px] uppercase tracking-widest text-[#555550] mb-4">Related Work</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/work/${r.slug}`}
                data-testid={PROJECT_DETAIL.relatedCard(r.slug)}
                className="group block border border-[#2A2A2A] hover:border-[#CCFF00] transition-colors"
              >
                <div className="relative w-full" style={{ aspectRatio: ratioToCss(r.aspectRatio) }}>
                  <img src={r.posterUrl} alt={r.title} loading="lazy" className="absolute inset-0 w-full h-full object-contain bg-black" />
                </div>
                <p className="p-2 font-mono text-[10px] uppercase tracking-widest text-[#888880] group-hover:text-[#CCFF00] truncate transition-colors">
                  {r.title}
                </p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
